import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MenuBar from './MenuBar';
import MenuToggleButton from './MenuToggleButton';
import { ProgressIcon } from './ProgressMessage';
import { getReport, findCoreLayer, findStandardFields } from '../utils/arcgis-network'; // This is overly expensive, should be able to get the core layer itself
import '../assets/styles/components/ReportSelectorDropDowns.css';
import '@esri/calcite-components/dist/components/calcite-button';
import '@esri/calcite-components/dist/components/calcite-dropdown';
import '@esri/calcite-components/dist/components/calcite-dropdown-group';
import '@esri/calcite-components/dist/components/calcite-dropdown-item';

import {
    CalciteButton,
    CalciteDropdown,
    CalciteDropdownGroup,
    CalciteDropdownItem
} from '@esri/calcite-components-react';
import { TargetedEvent } from '@esri/calcite-components';

const ReportSelectorDropDowns = ({
    reports = [],
    token = '',
    feature = '',
    onChange = (): void => {},
    pattern = '',
    includeGeoIdInPath = false,
    catalogUrl
}: {
    reports: ReportGeoItem[];
    token?: string | undefined;
    feature?: string | undefined;
    onChange?: (u: string, r: string | undefined, f: string | undefined) => void;
    pattern?: string | undefined | null;
    includeGeoIdInPath?: boolean | undefined;
    catalogUrl?: string | undefined;
}) => {
    const defReport = reports.find((r) => r.isDefault),
        defReportId =
            defReport !== undefined ? defReport.id : reports.length > 0 ? reports[0].id : '',
        defGeoId =
            defReport !== undefined ? defReport.geo : reports.length > 0 ? reports[0].geo : '',
        defGroupBy =
            defReport !== undefined
                ? defReport.groupFeaturesByField
                : reports.length > 0
                  ? reports[0].groupFeaturesByField
                  : undefined,
        [report, setReport] = useState<string>(defReportId),
        [coreLayer, setCoreLayer] = useState<string>(defGeoId),
        [groupByField, setGroupByField] = useState<string | undefined>(defGroupBy),
        [features, setFeatures] = useState<FeatureGroup[]>([]),
        [activeFeature, setActiveFeature] = useState<GeoFeature | null>(null),
        [working, setWorking] = useState<'no' | 'reports' | 'features' | 'yes'>('no'),
        activeReport =
            (coreLayer !== ''
                ? reports.find((r) => r.id === report && r.geo === coreLayer)
                : reports.find((r) => r.id === report)) || reports[0],
        { dataMode = 'report' } = activeReport,
        timerId = useRef(-1),
        notificationTimerId = useRef(-1),
        navigate = useNavigate();

    useEffect(() => {
        const handleChange = async () => {
            setWorking('features');
            setFeatures([]);
            setActiveFeature(null);
            const reportDetails: any = await getReport(report, token),
                { report: reportObj } = reportDetails,
                coreSrc = reportObj.data.sources[0],
                coreUrlPromise =
                    catalogUrl !== undefined
                        ? findCoreLayer(catalogUrl, coreLayer, undefined, token).then(
                              (urls: string[]) => {
                                  return urls[0];
                              }
                          )
                        : Promise.resolve(coreSrc.url),
                coreFeatureUrl = await coreUrlPromise;
            let idf = reportObj.design.idField,
                nmf = reportObj.design.nameField;
            const {
                idField: foundIdf,
                nameField: foundNmf,
                info: lyrInfo
            } = await findStandardFields(
                coreFeatureUrl,
                idf,
                nmf,
                undefined,
                undefined,
                undefined,
                token
            );
            if (idf !== foundIdf) {
                idf = foundIdf;
            }
            if (nmf !== foundNmf) {
                nmf = foundNmf;
            }
            const gbf =
                groupByField !== undefined &&
                lyrInfo.fields !== undefined &&
                lyrInfo.fields.findIndex(
                    (f: any) => f.name.toLowerCase() === groupByField.toLowerCase()
                ) >= 0
                    ? lyrInfo.fields.find(
                          (f: any) => f.name.toLowerCase() === groupByField.toLowerCase()
                      ).name
                    : undefined;
            const outf = gbf !== undefined ? [idf, nmf, gbf] : [idf, nmf];
            fetch(
                `${coreFeatureUrl}/query?f=json&outFields=${encodeURIComponent(
                    outf.join(',')
                )}&returnGeometry=false&where=${encodeURIComponent('1=1')}`
            )
                .then((featureRsp: any) => {
                    return featureRsp.json();
                })
                .then((featuresJson: any) => {
                    const featureSet: GeoFeature[] = featuresJson.features.map(
                        (f: any, i: number) => {
                            return {
                                id: f.attributes[idf],
                                name: f.attributes[nmf],
                                group: gbf !== undefined ? f.attributes[gbf] : '___defaultGroup'
                            };
                        }
                    );
                    featureSet.sort((a, b) => {
                        if (a.group !== undefined && b.group !== undefined) {
                            const d = a.group.localeCompare(b.group);
                            if (d !== 0) {
                                return d;
                            }
                        }
                        return a.name.localeCompare(b.name);
                    });
                    const groups = [...new Set(featureSet.map((f) => f.group))];
                    const featureGroups: FeatureGroup[] = groups.map((g) => {
                        return {
                            label: g ?? '___defaultGroup',
                            features: featureSet.filter((f) => f.group === g)
                        };
                    });
                    setFeatures(featureGroups);
                    const f =
                        (feature !== undefined && feature !== null && feature !== ''
                            ? featureSet.find((ff) => ff.id === feature)
                            : undefined) ?? featureSet[0];
                    setActiveFeature(f ?? null);
                })
                .finally(() => {
                    setWorking('no');
                });
        };
        handleChange();
    }, [report, token, feature, coreLayer, catalogUrl, groupByField]);

    const currFeatureId = activeFeature?.id;
    const currReportId = activeReport.id;
    const currGeoId = activeReport.geo;
    const activePath = includeGeoIdInPath
        ? `/view-${
              dataMode === 'static' ? 'publication' : 'report'
          }/${currReportId}/${currFeatureId}/${currGeoId}`
        : `/view-${
              dataMode === 'static' ? 'publication' : 'report'
          }/${currReportId}/${currFeatureId}`;

    useEffect(() => {
        // const firstLink: HTMLAnchorElement | null =
        //     activeId !== undefined && activeId !== null && activeId !== ''
        //         ? document.querySelector(
        //               `.rb-report-selector-controls .rb-features-menu > li[data-feature-id="${activeId}"] > a`
        //           )
        //         : document.querySelector(
        //               '.rb-report-selector-controls .rb-features-menu > li[data-feature-id] > a'
        //           );
        // //if ((activeFeature === undefined || activeFeature === null) && (firstLink !== null)) firstLink.click();
        // if (firstLink !== null) {
        if (timerId.current > 0) {
            window.clearTimeout(timerId.current);
        }
        if (currFeatureId !== undefined) {
            timerId.current = window.setTimeout(() => {
                //firstLink.click();
                navigate(activePath);
            }, 50);
        }
        // }
    }, [currFeatureId, activePath]);

    useEffect(() => {
        if (currFeatureId !== undefined && currFeatureId !== null) {
            const activeUrl = includeGeoIdInPath
                ? `/view-${
                      dataMode === 'static' ? 'publication' : 'report'
                  }/${currReportId}/${currFeatureId}/${currGeoId}`
                : `/view-${
                      dataMode === 'static' ? 'publication' : 'report'
                  }/${currReportId}/${currFeatureId}`;
            if (notificationTimerId.current > 0) {
                window.clearTimeout(notificationTimerId.current);
            }
            notificationTimerId.current = window.setTimeout(() => {
                onChange(activeUrl, currReportId, currFeatureId);
            }, 150);
        }
    }, [currFeatureId, currReportId, currGeoId, dataMode, onChange, includeGeoIdInPath]);

    const handleClick = (e: any, path: string, url: string, f?: GeoFeature) => {
        e.preventDefault();
        navigate(path);
        //if (onChange !== undefined) onChange(url, reports.find(r => r.id === report), f); // Handled by useEffect above...
    };

    return (
        <div className="ia-report-selector-controls rb-report-selector-controls">
            <div
                className={`ia-report-selector rb-report-selector ia-menu-container ${
                    reports.length === 1
                        ? 'ia-single-report rb-single-report'
                        : 'ia-multi-report rb-multi-report'
                }`}
            >
                <CalciteDropdown id="reportsMenu" className="rb-dropdown">
                    <CalciteButton
                        slot="trigger"
                        scale="l"
                        kind="neutral"
                        appearance="outline-fill"
                        alignment="icon-end-space-between"
                        iconStart="layers-f"
                        iconEnd="chevron-down"
                        width="full"
                    >
                        <span className="ia-report-name">{activeReport.label}</span>
                    </CalciteButton>
                    <CalciteDropdownGroup>
                        {reports.map((r, i) => {
                            return (
                                <CalciteDropdownItem
                                    key={i}
                                    onCalciteDropdownItemSelect={(e) => {
                                        e.preventDefault();
                                        setReport(r.id);
                                        setCoreLayer(r.geo);
                                        setGroupByField(r.groupFeaturesByField);
                                    }}
                                    className={`ia-report-selector-item rb-report-selector-item`}
                                    selected={
                                        r.id === activeReport.id && r.geo === activeReport.geo
                                            ? true
                                            : undefined
                                    }
                                >
                                    {r.label}
                                </CalciteDropdownItem>
                            );
                        })}
                    </CalciteDropdownGroup>
                </CalciteDropdown>
            </div>
            <div className="ia-feature-selector rb-feature-selector ia-menu-container">
                <CalciteDropdown id="featuresMenu" className="rb-dropdown">
                    <CalciteButton
                        slot="trigger"
                        scale="l"
                        kind="neutral"
                        appearance="outline-fill"
                        alignment="icon-end-space-between"
                        iconStart="pin-tear-f"
                        iconEnd="chevron-down"
                        width="full"
                    >
                        {working === 'yes' || working === 'features' ? (
                            <span className="inline-progress-holder">
                                <ProgressIcon />
                            </span>
                        ) : (
                            <span className="ia-feature-name rb-feature-name">
                                {activeFeature !== null ? activeFeature.name : ''}
                            </span>
                        )}
                    </CalciteButton>
                    {features.map((featureGroup) => {
                        return (
                            <CalciteDropdownGroup
                                groupTitle={
                                    featureGroup.label !== '___defaultGroup'
                                        ? featureGroup.label
                                        : undefined
                                }
                            >
                                {featureGroup.features.map((f: GeoFeature, i: number) => {
                                    const path = includeGeoIdInPath
                                            ? `/view-${
                                                  dataMode === 'static' ? 'publication' : 'report'
                                              }/${activeReport.id}/${f.id}/${activeReport.geo}`
                                            : `/view-${
                                                  dataMode === 'static' ? 'publication' : 'report'
                                              }/${activeReport.id}/${f.id}`,
                                        url =
                                            pattern !== undefined && pattern !== null
                                                ? pattern
                                                      .replace(/{report}/gi, activeReport.id)
                                                      .replace(/{feature}/gi, f.id)
                                                      .replace(/{geo}/gi, activeReport.geo)
                                                : '';
                                    return (
                                        <CalciteDropdownItem
                                            key={i}
                                            data-feature-id={f.id}
                                            onCalciteDropdownItemSelect={(e) => {
                                                setActiveFeature({ ...f });
                                                handleClick(e, path, url, f);
                                            }}
                                            className={`geo-feature-link ${
                                                currFeatureId === f.id ? 'geo-feature-active' : ''
                                            }`.trim()}
                                            selected={currFeatureId === f.id ? true : undefined}
                                        >
                                            {f.name}
                                        </CalciteDropdownItem>
                                    );
                                })}
                            </CalciteDropdownGroup>
                        );
                    })}
                </CalciteDropdown>
            </div>
        </div>
    );
};

export type ReportGeoItem = {
    id: string;
    geo: string;
    name: string | undefined;
    label: string | undefined;
    dataMode: string;
    isDefault: boolean;
    groupFeaturesByField?: string;
};

type GeoFeature = {
    id: string;
    name: string;
    group?: string;
};

type FeatureGroup = {
    label: string;
    features: GeoFeature[];
};

export default ReportSelectorDropDowns;
